"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export const CURRENT_LOCATION_FAILURE_MESSAGE =
  "We couldn’t use your current location. Search for a city or ZIP code instead.";

export function useCurrentLocationAction({ requestGpsLocation, onSuccess = null }) {
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const [currentLocationError, setCurrentLocationError] = useState(null);
  const mountedRef = useRef(true);
  const requestInFlightRef = useRef(false);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const clearCurrentLocationError = useCallback(() => {
    if (mountedRef.current) setCurrentLocationError(null);
  }, []);

  const requestCurrentLocation = useCallback(async () => {
    if (requestInFlightRef.current || typeof requestGpsLocation !== "function") return null;
    requestInFlightRef.current = true;
    if (mountedRef.current) {
      setCurrentLocationLoading(true);
      setCurrentLocationError(null);
    }

    try {
      const next = await requestGpsLocation({ force: true });
      if (!next) {
        if (mountedRef.current) setCurrentLocationError(CURRENT_LOCATION_FAILURE_MESSAGE);
        return null;
      }
      await onSuccess?.(next);
      return next;
    } catch {
      if (mountedRef.current) setCurrentLocationError(CURRENT_LOCATION_FAILURE_MESSAGE);
      return null;
    } finally {
      requestInFlightRef.current = false;
      if (mountedRef.current) setCurrentLocationLoading(false);
    }
  }, [onSuccess, requestGpsLocation]);

  return {
    currentLocationLoading,
    currentLocationError,
    clearCurrentLocationError,
    requestCurrentLocation,
  };
}
